import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Button, Checkbox } from '@mantine/core';

import { ClonotypeSizeSlider } from '../../clonotype-network-map/ClonotypeSizeSlider';
import { ColorClonotypeMapBy } from '../../clonotype-network-map/ColorClonotypeMapBy';
import { useNetworkMapClonotypesContext } from '../../clonotype-network-map/NetworkMapClonotypesContext';
import type { NetworkMapClonotype } from '../../clonotype-network-map/networkMap.utils';
import { InfoTooltip } from '../../common/InfoTooltip';
import {
  clearSelection,
  selectHideSingletons,
  selectSelectedSequences,
  setCollapsedClonotypes,
  setHideSingletons,
  setSelectedSequences,
} from '../../store/slices/antibodyAnalysis.slice';
import { defaultGridSize, setClonotypeSelection, setGridSize } from '../../store/slices/clonotypes.slice';
import { type AppDispatch, type AppState, useGetExperiment } from '../../store/store';

const INACTIVE_STYLE = { backgroundColor: '#969eae', color: 'white', border: 'none' };

const EMPTY_CLONOTYPE_IDS: number[] = [];

// A singleton clonotype has exactly one cell across all its nodes.
const isSingleton = (clonotype: NetworkMapClonotype) => clonotype.nodes.reduce((sum, node) => sum + node.cells.length, 0) === 1;
const s = {
  bar: 'flex flex-wrap items-center gap-x-3 gap-y-1 w-full px-4 py-1 text-sm bg-[rgba(235,246,242,0.35)] border-b border-[#e1e5e9]',
  statLabel: 'shrink-0 whitespace-nowrap font-semibold text-foreground',
  divider: 'shrink-0 text-[#c0c8d0] select-none',
  // Wraps like the bar itself, so without wrapping it would push Remove Singletons
  // past the bar's right edge on narrow screens instead of breaking onto a second line.
  rightGroup: 'flex flex-wrap items-center gap-x-4 gap-y-1 ml-auto',
} as const;
const GRID_SIZE_VALUES = Array.from({ length: 20 }, (_, i) => i + 1);

interface ControlBarProps {
  clonotypes: { id: number; sequences: { metadata: { sequenceId: string } }[] }[];
  isGridExpanded: boolean;
  onSelectAll?: () => void;
}

export function ControlBar({ clonotypes, isGridExpanded, onSelectAll }: ControlBarProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { id: experimentId } = useParams<{ id: string }>();
  const selectedSequencesArray = useSelector(selectSelectedSequences);
  const hideSingletons = useSelector(selectHideSingletons);
  const selectedClonotypes = useSelector((state: AppState) => state.clonotypes[experimentId!]?.selectedClonotypes ?? EMPTY_CLONOTYPE_IDS);
  const gridSize = useSelector((state: AppState) => state.clonotypes[experimentId!]?.gridSize ?? defaultGridSize);
  const { data: experiment } = useGetExperiment({ id: experimentId! });

  const setGridColumnCount = React.useCallback(
    (count: number) => {
      dispatch(setGridSize({ experimentId: experimentId!, gridSize: count }));
    },
    [dispatch, experimentId],
  );

  const totalClonotypeCount = clonotypes.length;
  const totalSequenceCount = clonotypes.reduce((total, c) => total + c.sequences.length, 0);

  const selectedSequencesSet = React.useMemo(() => new Set(selectedSequencesArray), [selectedSequencesArray]);
  const selectedClonotypeCount = React.useMemo(
    () => clonotypes.filter((c) => c.sequences.some((seq) => selectedSequencesSet.has(`${c.id}-${seq.metadata.sequenceId}`))).length,
    [clonotypes, selectedSequencesSet],
  );
  // Count only sequences whose clonotype is currently loaded (still selected in the map), so the
  // count tracks map selection both ways: unselecting a clonotype drops its sequences (its row
  // unloads) and re-selecting restores them. The keys stay in selectedSequences across the
  // round-trip, so individual sequence un-checks are preserved too.
  const selectedSequenceCount = React.useMemo(
    () => clonotypes.reduce((total, c) => total + c.sequences.filter((seq) => selectedSequencesSet.has(`${c.id}-${seq.metadata.sequenceId}`)).length, 0),
    [clonotypes, selectedSequencesSet],
  );
  const { clonotypes: networkClonotypes } = useNetworkMapClonotypesContext();
  // Whether the experiment has any singletons (clonotype with exactly 1 cell).
  const hasSingletons = React.useMemo(() => networkClonotypes.some(isSingleton), [networkClonotypes]);
  const visibleNetworkClonotypes = React.useMemo(() => {
    if (!hideSingletons) {
      return networkClonotypes;
    }
    return networkClonotypes.filter((c) => !isSingleton(c));
  }, [networkClonotypes, hideSingletons]);
  const visibleClonotypeIds = React.useMemo(() => visibleNetworkClonotypes.map((c) => c.clonotypeId), [visibleNetworkClonotypes]);
  const allSelected = visibleClonotypeIds.length > 0 && visibleClonotypeIds.every((id) => selectedClonotypes.includes(id));
  const someSelected = !allSelected && visibleClonotypeIds.some((id) => selectedClonotypes.includes(id));

  const handleSelectAllToggle = React.useCallback(() => {
    if (!experimentId) {
      return;
    }
    if (allSelected) {
      dispatch(setClonotypeSelection({ experimentId, clonotypes: [], cells: [], nodes: [] }));
      dispatch(clearSelection()); // clear export/inner-sequence selection too
      dispatch(setCollapsedClonotypes({}));
      return;
    }
    const allBarcodes = visibleNetworkClonotypes.flatMap((c) => c.nodes.flatMap((n) => n.cells.map((cell) => cell.barcode)));
    const allNodes = visibleNetworkClonotypes.flatMap((c) =>
      c.nodes.map((n) => ({
        clonotypeId: c.clonotypeId,
        nodeId: parseInt(n.id, 10),
        cellIds: n.cells.map((cell) => cell.cellId as number),
      })),
    );
    // Show the loader + reset the page's sequence-sync tracking before selecting, so the page's
    // effect re-checks every selected clonotype's sequences once the data loads.
    onSelectAll?.();
    dispatch(setClonotypeSelection({ experimentId, clonotypes: visibleClonotypeIds, cells: allBarcodes, nodes: allNodes }));
    // Expand all selected clonotypes (empty collapsed map = all expanded).
    dispatch(setCollapsedClonotypes({}));
  }, [allSelected, dispatch, experimentId, visibleClonotypeIds, visibleNetworkClonotypes, onSelectAll]);

  const handleHideSingletonsToggle = React.useCallback(() => {
    const willHide = !hideSingletons;
    dispatch(setHideSingletons(willHide));
    if (!willHide || !experimentId) {
      // Showing singletons again: just unhide the display; don't touch the selection.
      return;
    }
    // Hiding: drop singletons from the selection entirely (graph + export) so they're no longer
    // counted, shown, or exported — hiding them and unchecking them are the same action. The
    // remaining non-singletons stay selected, so "Select all" recomputes to checked.
    const singletonClonotypeIds = new Set(networkClonotypes.filter(isSingleton).map((c) => c.clonotypeId));
    if (singletonClonotypeIds.size === 0) {
      return;
    }
    const retainedClonotypeIds = selectedClonotypes.filter((id) => !singletonClonotypeIds.has(id));
    const retainedClonotypeIdSet = new Set(retainedClonotypeIds);
    const retainedClonotypes = networkClonotypes.filter((c) => retainedClonotypeIdSet.has(c.clonotypeId));
    const retainedCells = retainedClonotypes.flatMap((c) => c.nodes.flatMap((n) => n.cells.map((cell) => cell.barcode)));
    const retainedNodes = retainedClonotypes.flatMap((c) =>
      c.nodes.map((n) => ({ clonotypeId: c.clonotypeId, nodeId: parseInt(n.id, 10), cellIds: n.cells.map((cell) => cell.cellId as number) })),
    );
    dispatch(setClonotypeSelection({ experimentId, clonotypes: retainedClonotypeIds, cells: retainedCells, nodes: retainedNodes }));
    dispatch(setSelectedSequences(selectedSequencesArray.filter((key) => !singletonClonotypeIds.has(Number(key.split('-')[0])))));
  }, [hideSingletons, experimentId, networkClonotypes, selectedClonotypes, selectedSequencesArray, dispatch]);

  return (
    <div className={s.bar}>
      {/* Select all — operates on all clonotypes currently visible in the map (respects Hide Singletons). */}
      <Checkbox
        key={allSelected ? 'all' : someSelected ? 'some' : 'none'}
        label="Select all"
        size="xs"
        className="shrink-0"
        styles={{ label: { whiteSpace: 'nowrap' } }}
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAllToggle}
        disabled={visibleClonotypeIds.length === 0}
      />
      <span className={s.divider}>|</span>
      <span className={s.statLabel}>
        {selectedClonotypeCount > 0 ? `${selectedClonotypeCount} clonotypes selected` : `Total clonotypes: ${totalClonotypeCount}`}
      </span>
      <span className={s.divider}>|</span>
      <span className={s.statLabel}>
        {selectedSequenceCount > 0 ? `${selectedSequenceCount} sequences selected` : `Total Sequences: ${totalSequenceCount}`}
      </span>

      <div className={s.rightGroup}>
        {isGridExpanded && (
          <>
            <ClonotypeSizeSlider count={gridSize} setCount={setGridColumnCount} values={GRID_SIZE_VALUES} />
            <span className={s.divider}>|</span>
            <ColorClonotypeMapBy cellFileExists={Boolean(experiment?.cellFile)} />
          </>
        )}
        {/* Hide Singletons applies to both the network graph and the table, so it stays at the top */}
        <InfoTooltip label="No singletons in this experiment" disabled={hasSingletons} position="bottom">
          <Button
            size="compact-xs"
            variant={hideSingletons ? 'filled' : 'outline'}
            style={hideSingletons ? INACTIVE_STYLE : { borderColor: '#969eae', color: '#3d3d3d' }}
            disabled={!hasSingletons}
            onClick={handleHideSingletonsToggle}
          >
            {hideSingletons ? 'Add Singletons' : 'Remove Singletons'}
          </Button>
        </InfoTooltip>
      </div>
    </div>
  );
}
